import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  motion,
  useScroll,
  useSpring,
  AnimatePresence,
} from "framer-motion";

import {
  FiArrowUpRight,
  FiArrowRight,
  FiExternalLink,
  FiMail,
  FiMapPin,
  FiSend,
  FiPhone,
  FiCode,
  FiSmartphone,
  FiGlobe,
  FiClock,
} from "react-icons/fi";

import {
  FaReact,
  FaNodeJs,
  FaBootstrap,
  FaGithub,
  FaJava,
  FaInstagram,
  FaWhatsapp,
  FaLinkedin,
  FaGraduationCap,
} from "react-icons/fa";

import {
  SiTailwindcss,
  SiJavascript,
  SiTypescript,
  SiFramer,
  SiExpress,
  SiMongodb,
  SiFirebase,
  SiExpo,
  SiVite,
  SiCloudinary,
  SiLeetcode,
} from "react-icons/si";

import { BiLogoPlayStore } from "react-icons/bi";

import "./Hero.css";
import FooterKaranks1436 from "./Foot";
import Navbar from "./Navbar";
import Loader from "./Loader";

/* ============================================================
   DATA
============================================================ */

const SKILL_GROUPS = [
  {
    title: "Frontend",
    icon: <FiCode />,
    items: [
      { icon: <FaReact />, name: "React", color: "#61DAFB" },
      { icon: <SiJavascript />, name: "JavaScript", color: "#F7DF1E" },
      { icon: <SiTypescript />, name: "TypeScript", color: "#3178C6" },
      { icon: <SiTailwindcss />, name: "Tailwind CSS", color: "#38BDF8" },
      { icon: <FaBootstrap />, name: "Bootstrap", color: "#7952B3" },
      { icon: <SiFramer />, name: "Framer Motion", color: "#BB4BFD" },
      { icon: <SiVite />, name: "Vite", color: "#BD34FE" },
    ],
  },
  {
    title: "Backend & data",
    icon: <FiGlobe />,
    items: [
      { icon: <FaNodeJs />, name: "Node.js", color: "#339933" },
      { icon: <SiExpress />, name: "Express.js", color: "#EDEDED" },
      { icon: <SiMongodb />, name: "MongoDB", color: "#47A248" },
      { icon: <SiFirebase />, name: "Firebase", color: "#FFCA28" },
      { icon: <SiCloudinary />, name: "Cloudinary", color: "#6A8BFF" },
      { icon: <FiMail />, name: "Nodemailer", color: "#22B573" },
      { icon: <FiPhone />, name: "Twilio", color: "#F22F46" },
    ],
  },
  {
    title: "Mobile",
    icon: <FiSmartphone />,
    items: [
      { icon: <FaReact />, name: "React Native", color: "#61DAFB" },
      { icon: <SiExpo />, name: "Expo", color: "#F2F2F2" },
      { icon: <BiLogoPlayStore />, name: "Play Store", color: "#48FF48" },
      { icon: <SiFirebase />, name: "Firebase Auth", color: "#FFCA28" },
      { icon: <FiGlobe />, name: "Responsive UI", color: "#C8FF3D" },
      { icon: <FiSmartphone />, name: "Device testing", color: "#8B6BFF" },
    ],
  },
  {
    title: "Tools & more",
    icon: <FiArrowUpRight />,
    items: [
      { icon: <FaGithub />, name: "Git & GitHub", color: "#E6EDFF" },
      { icon: <FaJava />, name: "Java / JavaFX", color: "#ED8B00" },
      { icon: <FiCode />, name: "VS Code", color: "#3EA6F2" },
      { icon: <FiGlobe />, name: "Firebase Hosting", color: "#FFCA28" },
      { icon: <SiLeetcode />, name: "LeetCode", color: "#FFA116" },
      { icon: <FiArrowUpRight />, name: "Figma", color: "#F24E1E" },
    ],
  },
];

const PROJECTS = [
  {
    img: "/proj-realtycore.jpg",
    title: "RealtyCore",
    kicker: "Property platform",
    year: "2025",
    desc: "A property management platform with a modern desktop UI and a secure, REST-driven backend.",
    tags: ["JavaFX", "REST API", "MongoDB"],
    link: "/projects",
  },
  {
    img: "/proj-aushlink.jpg",
    title: "AushLink",
    kicker: "Social impact",
    year: "2025",
    desc: "A medical equipment & medicine donation platform connecting donors with people who need them.",
    tags: ["React.js", "Cloudinary", "CRUD"],
    link: "https://aushlink.web.app/",
    external: true,
  },
  {
    img: "/proj-darkhackers.jpg",
    title: "Dark Hackers",
    kicker: "Cyber security",
    year: "2024",
    desc: "An interactive, cinematic security-themed experience built around dark UI and motion.",
    tags: ["React", "Tailwind", "Firebase"],
    link: "https://dark-hackers-14.web.app/",
    external: true,
  },
  {
    img: "/proj-products.jpg",
    title: "Products App",
    kicker: "E-commerce",
    year: "2024",
    desc: "A fast product catalogue with instant search, filters and a friction-free cart flow.",
    tags: ["React", "Firebase", "Hooks"],
    link: "https://products-5dbf3.firebaseapp.com/",
    external: true,
  },
  {
    img: "/proj-listing.jpg",
    title: "Listing App",
    kicker: "Classifieds",
    year: "2024",
    desc: "A location-aware marketplace for posting, browsing and managing local listings.",
    tags: ["React", "Maps", "CRUD"],
    link: "https://listing-7621a.web.app/",
    external: true,
  },
  {
    img: "/proj-notes.jpg",
    title: "Dev Notes",
    kicker: "Notes & docs",
    year: "2024",
    desc: "A personal knowledge base where I publish snippets, breakdowns and what I learn while building.",
    tags: ["React", "Markdown", "Vite"],
    link: "/note",
  },
];

const SERVICES = [
  {
    icon: <FiGlobe />,
    title: "Web Development",
    desc: "Modern, responsive websites and web apps using React, Tailwind and Node.",
    img: "/about-workspace.jpg",
  },
  {
    icon: <FiSmartphone />,
    title: "App Development",
    desc: "Android & cross-platform apps with React Native and Expo.",
    img: "/proj-aushlink.jpg",
  },
  {
    icon: <FiCode />,
    title: "Software Development",
    desc: "Scalable, efficient custom tools and internal systems.",
    img: "/proj-realtycore.jpg",
  },
  {
    icon: <FiArrowUpRight />,
    title: "UI / UX Design",
    desc: "Clean, modern and user-friendly interfaces that feel effortless.",
    img: "/proj-products.jpg",
  },
  {
    icon: <FaGraduationCap />,
    title: "College Projects",
    desc: "Complete academic projects with Firebase, React or Java — documented.",
    img: "/proj-notes.jpg",
  },
  {
    icon: <FiExternalLink />,
    title: "Logo & Poster Design",
    desc: "Creative identities and posters for your brand or event.",
    img: "/proj-listing.jpg",
  },
  {
    icon: <BiLogoPlayStore />,
    title: "Video Editing",
    desc: "Cinematic edits, reels and promotional videos.",
    img: "/proj-darkhackers.jpg",
  },
  {
    icon: <FiPhone />,
    title: "Support & Maintenance",
    desc: "Ongoing care, fixes and improvements for existing projects.",
    img: "/about-workspace.jpg",
  },
];

const CERTIFICATES = [
  {
    title: "Google Certificate",
    img: "/cert-credential.jpg",
    tag: "Certification",
    href: "https://api.accredible.com/v1/frontend/credential_website_embed_image/certificate/144136801",
  },
  {
    title: "Hackathon Certificate",
    img: "/cert-hackathon.jpg",
    tag: "Competition",
    href: "https://d8it4huxumps7.cloudfront.net/lambda-pdfs/certificate-images/9d77f849-205d-42ae-b53d-f675648839de.jpg",
  },
  {
    title: "Dark Hackers",
    img: "/proj-darkhackers.jpg",
    tag: "Project",
    href: "https://dark-hackers-14.web.app/",
  },
  {
    title: "Products App",
    img: "/proj-products.jpg",
    tag: "Project",
    href: "https://products-5dbf3.firebaseapp.com/",
  },
];

const EDUCATION = [
  {
    period: "2024 — 2027",
    title: "Bachelor of Computer Applications",
    org: "D.A.V College, Bathinda",
    desc: "Building strong computer-science fundamentals while shipping real projects on the side.",
    badge: "Education",
  },
  {
    period: "2024 — Present",
    title: "Freelance client work",
    org: "Web & app projects",
    desc: "RealtyCore, AushLink and client builds with React, Java and Firebase — delivered end to end.",
    badge: "Freelance",
  },
  {
    period: "2022 — 2023",
    title: "Senior secondary — 12th",
    org: "Lord Rama Sr. Sec. School, Bathinda",
    desc: "Computer science focus — where the curiosity for building software started.",
    badge: "School",
  },
  {
    period: "2020 — Present",
    title: "B.K.M Companies",
    org: "Self-taught developer",
    desc: "Teaching myself to engineer web & mobile apps — experiments, side projects and consistency.",
    badge: "Self-taught",
  },
];

const SOCIALS = [
  { icon: <FaGithub />, href: "https://github.com/Karanks1436", label: "GitHub" },
  { icon: <FaLinkedin />, href: "https://linkedin.com", label: "LinkedIn" },
  { icon: <FaInstagram />, href: "https://www.instagram.com/karanks1436", label: "Instagram" },
  { icon: <FiMail />, href: "mailto:karanksxxx@gmail.com", label: "Email" },
  { icon: <FaWhatsapp />, href: "https://wa.me/919041429065", label: "WhatsApp" },
  { icon: <SiLeetcode />, href: "https://leetcode.com/u/karanks1436/", label: "LeetCode" },
];

const STATS = [
  { n: 6, suffix: "+", label: "Projects built" },
  { n: 18, suffix: "+", label: "Technologies" },
  { n: 1, suffix: "+", label: "Years learning" },
  { n: 100, suffix: "%", label: "Dedication" },
];

/* ============================================================
   HOOKS
============================================================ */

/* typewriter */
function useTypewriter(words, { typeSpeed = 70, deleteSpeed = 40, delay = 1800 } = {}) {
  const [text, setText] = useState("");
  const [index, setIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!words?.length) return;
    const word = words[index % words.length];
    let timer;

    if (!deleting && text === word) {
      timer = setTimeout(() => setDeleting(true), delay);
    } else if (deleting && text === "") {
      setDeleting(false);
      setIndex((p) => p + 1);
    } else {
      timer = setTimeout(
        () => setText(word.slice(0, text.length + (deleting ? -1 : 1))),
        deleting ? deleteSpeed : typeSpeed
      );
    }
    return () => clearTimeout(timer);
  }, [text, deleting, index, words, typeSpeed, deleteSpeed, delay]);

  return text;
}

/* live IST clock */
function useClock() {
  const [time, setTime] = useState("--:--:--");
  useEffect(() => {
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const tick = () => setTime(fmt.format(new Date()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

/* counters */
function Counter({ to, suffix = "", className = "" }) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      setStarted(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setValue(to);
      return;
    }
    const duration = 1500;
    const t0 = performance.now();
    let frame;
    const step = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setValue(Math.round(to * eased));
      if (p < 1) frame = requestAnimationFrame(step);
      else setValue(to);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [started, to]);

  return (
    <span ref={ref} className={className}>
      {value}
      {suffix}
    </span>
  );
}

/* magnetic wrapper */
function Magnetic({ children, strength = 12, className = "" }) {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el || window.matchMedia("(hover: none)").matches) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left - r.width / 2) / (r.width / 2);
    const y = (e.clientY - r.top - r.height / 2) / (r.height / 2);
    el.style.transform = `translate(${(x * strength).toFixed(1)}px, ${(y * strength).toFixed(1)}px)`;
  };

  const reset = () => {
    if (ref.current) ref.current.style.transform = "";
  };

  return (
    <span
      ref={ref}
      className={`kk-magnetic ${className}`}
      onMouseMove={onMove}
      onMouseLeave={reset}
    >
      {children}
    </span>
  );
}

/* ============================================================
   HOME
   ------------------------------------------------------------
   Navbar and Footer are deliberately NOT in this file — they
   already live in their own components. Compose them around it:

       <Navbar />
       <Home />      ← this file
       <Foot />

   Section ids for the nav links:
   #home  #about  #work  #skills  #certificates
   #services  #journey  #contact
============================================================ */

export default function Hero() {
  const rootRef = useRef(null);
  const heroRef = useRef(null);
  const curtainRef = useRef(null);
  const curtainImgRef = useRef(null);
  const copyRef = useRef(null);
  const visualRef = useRef(null);
  const statsRef = useRef(null);
  const cueRef = useRef(null);
  const previewRef = useRef(null);
  const techPreviewRef = useRef(null);

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [tech, setTech] = useState(null);

  const typed = useTypewriter(
    ["SOFTWARE ENGINEER", "WEB DEVELOPER", "APP DEVELOPER"],
    { typeSpeed: 65, deleteSpeed: 38, delay: 1700 }
  );
  const clock = useClock();

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  /* ---------------------------------------------------------
     PARALLAX ENGINE — one rAF loop for every [data-plx]
     --------------------------------------------------------- */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const items = Array.from(root.querySelectorAll("[data-plx]"));
    if (!items.length || reduce) return;

    let pointerX = 0;
    let pointerY = 0;
    let raf = 0;

    const onPointer = (e) => {
      pointerX = e.clientX / window.innerWidth - 0.5;
      pointerY = e.clientY / window.innerHeight - 0.5;
    };

    const render = () => {
      const vh = window.innerHeight;

      items.forEach((el) => {
        const speed = parseFloat(el.dataset.plx) || 0;
        const xSpeed = parseFloat(el.dataset.plxX) || 0;
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -420 || rect.top > vh + 420) return;

        const y = (rect.top + rect.height / 2 - vh / 2) * -speed;
        const x = xSpeed ? (rect.left + rect.width / 2 - window.innerWidth / 2) * -xSpeed : 0;
        el.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`;
      });

      /* hero pointer parallax */
      if (fine) {
        root.querySelectorAll("[data-mouse]").forEach((el) => {
          const s = parseFloat(el.dataset.mouse) || 10;
          el.style.transform = `translate3d(${(pointerX * s).toFixed(2)}px, ${(pointerY * s * 0.8).toFixed(2)}px, 0)`;
        });
      }

      /* hero stage — the portrait opens into a full-bleed curtain,
         then the rest of the page slides over it */
      const stage = heroRef.current;
      if (stage) {
        const runway = stage.offsetHeight - vh;
        if (runway > 80) {
          const p = Math.min(1, Math.max(0, -stage.getBoundingClientRect().top / runway));
          const e = p * p * (3 - 2 * p);

          if (curtainRef.current) {
            curtainRef.current.style.opacity = String(
              Math.min(1, Math.max(0, (p - 0.06) / 0.46))
            );
          }
          if (curtainImgRef.current) {
            curtainImgRef.current.style.transform = `translate3d(0, ${(-p * 5).toFixed(2)}%, 0) scale(${(
              1.18 - 0.18 * e
            ).toFixed(3)})`;
          }
          if (copyRef.current) {
            copyRef.current.style.opacity = String(Math.max(0, 1 - p * 2.1));
            copyRef.current.style.transform = `translate3d(0, ${(-p * 76).toFixed(1)}px, 0)`;
          }
          if (visualRef.current) {
            visualRef.current.style.opacity = String(Math.max(0, 1 - p * 1.7));
            visualRef.current.style.transform = `translate3d(0, ${(-p * 120).toFixed(
              1
            )}px, 0) scale(${(1 + p * 0.26).toFixed(3)})`;
          }
          if (statsRef.current) {
            statsRef.current.style.opacity = String(Math.max(0, 1 - p * 3.2));
            statsRef.current.style.transform = `translate3d(0, ${(p * 26).toFixed(1)}px, 0)`;
          }
          if (cueRef.current) {
            cueRef.current.style.opacity = String(Math.max(0, 1 - p * 2.6));
          }
        }
      }

      raf = requestAnimationFrame(render);
    };

    if (fine) window.addEventListener("mousemove", onPointer, { passive: true });
    raf = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onPointer);
    };
  }, []);

  /* service hover preview (desktop only) */
  const showPreview = (service, e) => {
    const el = previewRef.current;
    if (!el || window.innerWidth <= 1024) return;
    el.querySelector("img").src = service.img;
    el.querySelector("span").textContent = service.title;
    el.style.left = `${e.clientX}px`;
    el.style.top = `${e.clientY}px`;
    el.classList.add("is-on");
  };
  const movePreview = (e) => {
    const el = previewRef.current;
    if (!el) return;
    el.style.left = `${e.clientX}px`;
    el.style.top = `${e.clientY}px`;
  };
  const hidePreview = () => previewRef.current?.classList.remove("is-on");

  /* tech-stack hover — same idea as services, but the preview is the icon */
  const showTech = (skill, e) => {
    if (window.innerWidth <= 1024) return;
    setTech({ name: skill.name, color: skill.color, icon: skill.icon });
    const el = techPreviewRef.current;
    if (!el) return;
    el.style.left = `${e.clientX}px`;
    el.style.top = `${e.clientY}px`;
    el.classList.add("is-on");
  };
  const moveTech = (e) => {
    const el = techPreviewRef.current;
    if (!el) return;
    el.style.left = `${e.clientX}px`;
    el.style.top = `${e.clientY}px`;
  };
  const hideTech = () => techPreviewRef.current?.classList.remove("is-on");

  /* jump to a page inside a deck (used by the index dots) */
  const goToPage = (deckId, idx) => {
    const deck = rootRef.current?.querySelector(`#${deckId}`);
    const head = deck?.querySelector(".kk-deck__head");
    if (!deck || !head) return;
    window.scrollTo({
      top: deck.offsetTop + head.offsetHeight + idx * window.innerHeight,
      behavior: "smooth",
    });
  };

  /* contact form → mailto */
  const submitForm = (event) => {
    event.preventDefault();
    const subject = `Project enquiry from ${form.name}`;
    const body = `${form.message}\n\n— ${form.name} (${form.email})`;
    window.location.href = `mailto:karanksxxx@gmail.com?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    setSent(true);
    setTimeout(() => setSent(false), 3500);
  };

  const marqueeWords = useMemo(
    () => ["React", "TypeScript", "Node.js", "React Native", "MongoDB", "Firebase", "Tailwind", "Express", "Java", "Git"],
    []
  );

  return (
    <div className="kk-root" ref={rootRef}>
     
      {/* scroll progress */}
      <motion.div className="kk-progress" style={{ scaleX: progress }} />

      {/* ============================================================
          HERO
      ============================================================ */}
      <section className="kk-hero-stage" id="home" ref={heroRef}>
        <div className="kk-hero">
          <div className="kk-hero__bg" aria-hidden="true">
          <div className="kk-hero__grid" data-plx="0.05" />
          <div className="kk-hero__glow kk-hero__glow--acid" data-plx="0.18" />
          <div className="kk-hero__glow kk-hero__glow--violet" data-plx="0.26" />
          <div className="kk-hero__glow kk-hero__glow--cyan" data-plx="0.1" />
          <div className="kk-hero__word" data-plx="0.12">
            KKS<span>·</span>1436
            </div>
          </div>

          {/* the small portrait melts into this full-bleed layer on scroll */}
          <div className="kk-hero__curtain" ref={curtainRef} aria-hidden="true">
            <img ref={curtainImgRef} src="/hero-portrait.jpg" alt="" />
            <span className="kk-hero__curtain-veil" />
          </div>

          <div className="kk-wrap kk-hero__inner">
            <div className="kk-hero__copy" ref={copyRef}>
            <motion.div
              className="kk-kicker"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
            >
              <span className="kk-dot" />
              <span>Available for freelance</span>
              <span className="kk-slash">/</span>
              <span>Bathinda, IN</span>
              <span className="kk-slash">/</span>
              <span className="kk-clock">
                <FiClock /> {clock} IST
              </span>
            </motion.div>

            <h1 className="kk-title">
              <span className="kk-title__line">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  Karan
                </motion.span>
              </span>
              <span className="kk-title__line">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.19, ease: [0.22, 1, 0.36, 1] }}
                >
                  Kumar<span className="kk-acid">.</span>
                </motion.span>
              </span>
              <span className="kk-title__line">
                <motion.span
                  className="kk-serif"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
                >
                  Better code, bigger dreams.
                </motion.span>
              </span>
            </h1>

            <motion.p
              className="kk-roles"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              <span className="kk-roles__typed">{typed}</span>
              <span className="kk-caret">|</span>
            </motion.p>

            <motion.p
              className="kk-hero__desc"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.48 }}
            >
              <strong>Software engineer &amp; developer</strong> crafting modern web and
              mobile applications that solve real-world problems — obsessed with clean
              code, creative UI and turning ideas into digital products.
            </motion.p>

            <motion.div
              className="kk-hero__cta"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.56 }}
            >
              <Magnetic>
                <a className="kk-btn kk-btn--lg" href="/projects">
                  View my work
                  <FiArrowUpRight />
                </a>
              </Magnetic>
              <Magnetic>
                <a className="kk-btn kk-btn--ghost kk-btn--lg" href="/contact">
                  Start a project
                  <FiArrowRight />
                </a>
              </Magnetic>
            </motion.div>

            <motion.div
              className="kk-socials"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.64 }}
            >
              <span className="kk-socials__label">Elsewhere</span>
              {SOCIALS.slice(0, 4).map((s) => (
                <a
                  key={s.label}
                  className="kk-social"
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                >
                  {s.icon}
                  <span>{s.label}</span>
                </a>
              ))}
            </motion.div>
          </div>

            <div className="kk-hero__visual" ref={visualRef}>
            <div className="kk-portrait-tilt" data-mouse="14">
            <motion.figure
              className="kk-portrait"
              initial={{ opacity: 0, scale: 0.94, y: 26 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <img src="/hero-portrait.jpg" alt="Karan Kumar" width="960" height="1117" />
              <div className="kk-portrait__frame" />
              <figcaption className="kk-portrait__cap">
                <div>
                  <b>Karan Kumar</b>
                  <span>KKS · 1436</span>
                </div>
                <span className="kk-portrait__status">
                  <i /> Open to work
                </span>
              </figcaption>
            </motion.figure>
            </div>

            <div className="kk-chip kk-chip--tl" data-plx="0.08">
              <i /> React · Node.js
            </div>
            <div className="kk-chip kk-chip--br" data-plx="0.14">
              <i className="kk-chip__v" /> Firebase
            </div>
          </div>
        </div>

          {/* stats bar */}
          <div className="kk-stats" ref={statsRef}>
          <div className="kk-wrap kk-stats__inner">
            {STATS.map((s) => (
              <div className="kk-stat" key={s.label}>
                <span className="kk-stat__num">
                  <Counter to={s.n} />
                  <em>{s.suffix}</em>
                </span>
                <span className="kk-stat__label">{s.label}</span>
              </div>
            ))}
            </div>
          </div>

          <div className="kk-hero__cue" ref={cueRef} aria-hidden="true">
            <span className="kk-hero__cue-track"><i /></span>
            <b>Scroll to open</b>
          </div>
        </div>
      </section>

      {/* ============================================================
          MARQUEE
      ============================================================ */}
      <div className="kk-marquee" aria-hidden="true">
        <div className="kk-marquee__track">
          {[0, 1].map((k) => (
            <div className="kk-marquee__item" key={k}>
              {marqueeWords.map((w) => (
                <span key={`${k}-${w}`}>
                  {w} <i>◆</i>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ============================================================
          ABOUT
      ============================================================ */}
      <section className="kk-section" id="about">
        <div className="kk-wrap kk-split">
          <motion.div
            className="kk-about__media"
            initial={{ opacity: 0, x: -36 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="kk-frame">
              <img src="/about-workspace.jpg" alt="Karan's workspace" data-plx="0.05" loading="lazy" />
            </div>
            <span className="kk-frame__code">~/night-shift.js</span>
            <div className="kk-frame__badge">
              <span>Currently</span>
              <b>BCA · 2024 – 2027</b>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 36 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="kk-eyebrow">01 — About me</span>
            <h2 className="kk-h2">
              Turning ideas into
              <br />
              <span className="kk-serif kk-acid">real products</span>
            </h2>
            <p className="kk-lead">
              I'm Karan Kumar, a software engineer and BCA student who loves building
              modern web &amp; mobile applications, exploring new technologies and
              solving real-world problems with code.
            </p>
            <p className="kk-lead">
              I believe in continuous learning, creativity and consistency — small steps
              every day, shipped with care, compound into products people actually enjoy
              using.
            </p>

            <div className="kk-facts">
              <div className="kk-fact">
                <span>Base</span>
                <b>Bathinda, Punjab</b>
              </div>
              <div className="kk-fact">
                <span>Studying</span>
                <b>BCA · D.A.V College</b>
              </div>
              <div className="kk-fact">
                <span>Email</span>
                <b>karanksxxx@gmail.com</b>
              </div>
              <div className="kk-fact">
                <span>Focus</span>
                <b>Web · Mobile · UI/UX</b>
              </div>
            </div>

            <div className="kk-tags">
              <span className="kk-tag kk-tag--acid">Open to freelance</span>
              <span className="kk-tag">Problem solver</span>
              <span className="kk-tag">Fast learner</span>
              <span className="kk-tag">Self-taught since 2020</span>
            </div>

            <div className="kk-flow">
              <b>Code</b>
              <FiArrowRight />
              <b>Build</b>
              <FiArrowRight />
              <b className="kk-acid">Grow</b>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================================
          WORK — full-width pages, stacked like a diary
      ============================================================ */}
      <section className="kk-deck" id="work" aria-label="Featured projects">
        <header className="kk-deck__head kk-wrap">
          <div>
            <span className="kk-eyebrow">02 — Selected work</span>
            <h2 className="kk-h2">
              Shipped, usable,
              <br />
              <span className="kk-serif kk-acid">real.</span>
            </h2>
          </div>
          <div className="kk-sec-head__aside">
            <p>
              Six builds that turned a problem into a product. Turn the pages — each
              project gets a spread of its own.
            </p>
            <span className="kk-deck__count">
              <b>01</b> / 06 · pages
            </span>
          </div>
        </header>

        {PROJECTS.map((p, i) => (
          <article className="kk-page" key={p.title} style={{ top: i * 22 }}>
            <div className={`kk-page__card${i % 2 ? " kk-page__card--flip" : ""}`}>
              <div className="kk-page__media">
                <img src={p.img} alt={p.title} />
                <span className="kk-page__no">{String(i + 1).padStart(2, "0")}</span>

                {/* 3D blueprint that lifts off the card on hover */}
                <div className="kk-bp" aria-hidden="true">
                  <span className="kk-bp__grid" />
                  <i className="kk-bp__corner kk-bp__corner--tl" />
                  <i className="kk-bp__corner kk-bp__corner--tr" />
                  <i className="kk-bp__corner kk-bp__corner--bl" />
                  <i className="kk-bp__corner kk-bp__corner--br" />
                  <span className="kk-bp__dim kk-bp__dim--x">
                    <em>1440</em>
                  </span>
                  <span className="kk-bp__dim kk-bp__dim--y">
                    <em>responsive</em>
                  </span>
                  <div className="kk-bp__spec">
                    <b>FIG.{String(i + 1).padStart(2, "0")}</b>
                    <span>{p.kicker}</span>
                    <span>{p.tags.join(" · ")}</span>
                  </div>
                </div>

                <a
                  className="kk-page__open"
                  href={p.link}
                  target={p.external ? "_blank" : undefined}
                  rel={p.external ? "noopener noreferrer" : undefined}
                  aria-label={`Open ${p.title}`}
                >
                  <FiArrowUpRight />
                </a>
              </div>

              <div className="kk-page__body">
                <span className="kk-page__kicker">
                  {p.kicker}
                  <b>{p.year}</b>
                </span>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                <div className="kk-work__tags">
                  {p.tags.map((t) => (
                    <span key={t}>{t}</span>
                  ))}
                </div>
                <div className="kk-page__spec">
                  <div>
                    <span>Role</span>
                    <b>Design · Build</b>
                  </div>
                  <div>
                    <span>Scope</span>
                    <b>{p.kicker}</b>
                  </div>
                  <div>
                    <span>Shipped</span>
                    <b>{p.year}</b>
                  </div>
                </div>
                <nav className="kk-page__dots" aria-label="All projects">
                  {PROJECTS.map((q, qi) => (
                    <button
                      key={q.title}
                      type="button"
                      className={qi === i ? "is-on" : ""}
                      onClick={() => goToPage("work", qi)}
                      aria-label={`Go to ${q.title}`}
                      aria-current={qi === i ? "true" : undefined}
                    >
                      {String(qi + 1).padStart(2, "0")}
                    </button>
                  ))}
                </nav>
                <a
                  className="kk-btn kk-btn--ghost kk-page__cta"
                  href={p.link}
                  target={p.external ? "_blank" : undefined}
                  rel={p.external ? "noopener noreferrer" : undefined}
                >
                  View project
                  <FiArrowRight />
                </a>
                <span className="kk-page__folio">
                  {String(i + 1).padStart(2, "0")} — {String(PROJECTS.length).padStart(2, "0")}
                </span>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* ============================================================
          SKILLS
      ============================================================ */}
      <section className="kk-section" id="skills">
        <div className="kk-ghost" data-plx="0.09" aria-hidden="true">
          STACK
        </div>
        <div className="kk-wrap">
          <div className="kk-sec-head">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7 }}
            >
              <span className="kk-eyebrow">03 — Stack</span>
              <h2 className="kk-h2">
                Technologies I
                <br />
                <span className="kk-serif kk-acid">work with</span>
              </h2>
            </motion.div>
            <motion.div
              className="kk-sec-head__aside"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <p>
                Modern tools for fast, scalable and user-friendly applications. Here's
                what's usually open on my machine.
              </p>
              <a className="kk-link" href="/codeupload">
                View codes
                <FiArrowUpRight />
              </a>
            </motion.div>
          </div>

          <div className="kk-board">
            {SKILL_GROUPS.map((group, gi) => (
              <motion.div
                className="kk-board__col"
                key={group.title}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.6, delay: gi * 0.06 }}
              >
                <div className="kk-board__head">
                  {group.icon}
                  <h3>{group.title}</h3>
                </div>
                <ul>
                  {group.items.map((s) => (
                    <li
                      key={s.name}
                      onMouseEnter={(e) => showTech(s, e)}
                      onMouseMove={moveTech}
                      onMouseLeave={hideTech}
                    >
                      <span className="kk-board__ic" style={{ color: s.color }}>
                        {s.icon}
                      </span>
                      <span className="kk-board__name">{s.name}</span>
                      <i className="kk-board__go">
                        <FiArrowUpRight />
                      </i>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* icon preview that follows the cursor */}
        <div className="kk-techpreview" ref={techPreviewRef} aria-hidden="true">
          <span className="kk-techpreview__ic" style={{ color: tech ? tech.color : "#c8ff3d" }}>
            {tech ? tech.icon : null}
          </span>
          <b>{tech ? tech.name : "Tech stack"}</b>
          <span className="kk-techpreview__hint">in my toolkit</span>
        </div>
      </section>

      {/* ============================================================
          CERTIFICATES — mosaic, foil-framed
      ============================================================ */}
      <section className="kk-section kk-section--tight" id="certificates">
        <div className="kk-certs-bg" aria-hidden="true">
          <span data-plx="0.07" />
          <span data-plx="0.16" />
        </div>

        <div className="kk-wrap">
          <div className="kk-sec-head">
            <div>
              <span className="kk-eyebrow">04 — Proof</span>
              <h2 className="kk-h2">
                Certificates &amp;
                <br />
                <span className="kk-serif kk-acid">highlights</span>
              </h2>
            </div>
            <div className="kk-sec-head__aside">
              <p>
                Certifications, competition wins and shipped side projects — the receipts
                behind the résumé. Hover one to read it properly.
              </p>
            </div>
          </div>

          <div className="kk-certs">
            {CERTIFICATES.map((c, i) => (
              <motion.a
                className={`kk-cert${i === 0 ? " kk-cert--hero" : ""}`}
                key={c.title}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: Math.min(i * 0.07, 0.28) }}
              >
                <span className="kk-cert__media">
                  <img src={c.img} alt={c.title} data-plx="0.05" loading="lazy" />
                </span>
                <span className="kk-cert__scrim" aria-hidden="true" />
                <span className="kk-cert__foil" aria-hidden="true" />

                <span className="kk-cert__kind">{c.tag}</span>
                <span className="kk-cert__idx">0{i + 1}</span>

                <span className="kk-cert__body">
                  <span className="kk-cert__txt">
                    <b>{c.title}</b>
                    <span>{c.tag === "Project" ? "Live project" : "Verified credential"}</span>
                  </span>
                  <i className="kk-cert__go">
                    <FiArrowUpRight />
                  </i>
                </span>
              </motion.a>
            ))}

            <motion.a
              className="kk-cert kk-cert--more"
              href="https://github.com/Karanks1436"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.28 }}
            >
              <span className="kk-cert__plus">+</span>
              <span className="kk-cert__txt">
                <b>More on GitHub</b>
                <span>Every build, experiment &amp; repo</span>
              </span>
              <i className="kk-cert__go">
                <FiArrowRight />
              </i>
            </motion.a>
          </div>
        </div>
      </section>

      {/* ============================================================
          SERVICES
      ============================================================ */}
      <section className="kk-section" id="services">
        <div className="kk-wrap">
          <div className="kk-sec-head">
            <div>
              <span className="kk-eyebrow kk-eyebrow--violet">05 — Services</span>
              <h2 className="kk-h2">
                What I can
                <br />
                <span className="kk-serif">build for you</span>
              </h2>
            </div>
            <div className="kk-sec-head__aside">
              <p>
                From a single landing page to a full product — design, build and ship
                with one person who cares about the details.
              </p>
              <a className="kk-link" href="/services">
                All services
                <FiArrowUpRight />
              </a>
            </div>
          </div>

          <div className="kk-services">
            {SERVICES.map((s, i) => (
              <a
                className="kk-service"
                key={s.title}
                href="/services"
                onMouseEnter={(e) => showPreview(s, e)}
                onMouseMove={movePreview}
                onMouseLeave={hidePreview}
              >
                <span className="kk-service__idx">{String(i + 1).padStart(2, "0")}</span>
                <span className="kk-service__title">
                  <span className="kk-service__ic">{s.icon}</span>
                  {s.title}
                </span>
                <span className="kk-service__desc">{s.desc}</span>
                <span className="kk-service__arrow">
                  <FiArrowUpRight />
                </span>
              </a>
            ))}
          </div>
        </div>

        <div className="kk-preview" ref={previewRef} aria-hidden="true">
          <img src="/about-workspace.jpg" alt="" />
          <span>Web Development</span>
        </div>
      </section>

      {/* ============================================================
          JOURNEY — same diary pages as the work deck
      ============================================================ */}
      <section className="kk-deck kk-deck--journey" id="journey" aria-label="Journey">
        <header className="kk-deck__head kk-wrap">
          <div>
            <span className="kk-eyebrow kk-eyebrow--violet">06 — Journey</span>
            <h2 className="kk-h2">
              My journey
              <br />
              <span className="kk-serif">so far</span>
            </h2>
          </div>
          <div className="kk-sec-head__aside">
            <p>
              Learning in public since 2020 — from the first <code>&lt;div&gt;</code> to
              shipping products for clients. One chapter per page.
            </p>
            <div className="kk-deck__stats">
              {STATS.map((st) => (
                <div key={st.label}>
                  <b>
                    <Counter to={st.n} />
                    <em>{st.suffix}</em>
                  </b>
                  <span>{st.label}</span>
                </div>
              ))}
            </div>
            <span className="kk-deck__count">
              <b>01</b> / 04 · chapters
            </span>
          </div>
        </header>

        {EDUCATION.map((item, i) => (
          <article className="kk-page" key={`${item.title}-${i}`} style={{ top: i * 22 }}>
            <div className={`kk-page__card${i % 2 ? " kk-page__card--flip" : ""}`}>
              <div className="kk-page__media kk-page__media--plate">
                <div className="kk-plate" aria-hidden="true">
                  <span className="kk-plate__grid" data-plx="0.05" />
                  <span className="kk-plate__glow" data-plx="0.14" />
                  <span className="kk-plate__ring" />
                  <span className="kk-plate__ring kk-plate__ring--2" />
                  <span
                    className="kk-plate__arc"
                    style={{ "--p": ((i + 1) / EDUCATION.length) * 100 }}
                  />
                  <span className="kk-plate__year" data-plx="0.08">
                    {item.period.slice(0, 4)}
                  </span>
                  <span className="kk-plate__label">
                    CH.{String(i + 1).padStart(2, "0")} — {item.badge}
                  </span>
                  <span className="kk-plate__org">{item.org}</span>
                  <span className="kk-plate__tick kk-plate__tick--a" />
                  <span className="kk-plate__tick kk-plate__tick--b" />
                  <span className="kk-plate__tick kk-plate__tick--c" />
                  <span className="kk-plate__tick kk-plate__tick--d" />
                </div>

                {/* same blueprint lift as the project pages */}
                <div className="kk-bp" aria-hidden="true">
                  <span className="kk-bp__grid" />
                  <i className="kk-bp__corner kk-bp__corner--tl" />
                  <i className="kk-bp__corner kk-bp__corner--tr" />
                  <i className="kk-bp__corner kk-bp__corner--bl" />
                  <i className="kk-bp__corner kk-bp__corner--br" />
                  <span className="kk-bp__dim kk-bp__dim--x">
                    <em>{item.period}</em>
                  </span>
                  <span className="kk-bp__dim kk-bp__dim--y">
                    <em>{item.badge}</em>
                  </span>
                  <div className="kk-bp__spec">
                    <b>CH.{String(i + 1).padStart(2, "0")}</b>
                    <span>{item.org}</span>
                    <span>{item.period}</span>
                  </div>
                </div>

                <span className="kk-page__no">{String(i + 1).padStart(2, "0")}</span>
                <a className="kk-page__open" href="/about" aria-label="More about Karan">
                  <FiArrowUpRight />
                </a>
              </div>

              <div className="kk-page__body">
                <span className="kk-page__kicker">
                  {item.badge}
                  <b>{item.period}</b>
                </span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
                <div className="kk-work__tags">
                  <span>{item.org}</span>
                </div>
                <div className="kk-page__spec">
                  <div>
                    <span>Period</span>
                    <b>{item.period}</b>
                  </div>
                  <div>
                    <span>Where</span>
                    <b>{item.org}</b>
                  </div>
                  <div>
                    <span>Chapter</span>
                    <b>
                      {String(i + 1).padStart(2, "0")} / {String(EDUCATION.length).padStart(2, "0")}
                    </b>
                  </div>
                </div>
                <nav className="kk-page__dots" aria-label="All chapters">
                  {EDUCATION.map((q, qi) => (
                    <button
                      key={q.title}
                      type="button"
                      className={qi === i ? "is-on" : ""}
                      onClick={() => goToPage("journey", qi)}
                      aria-label={`Go to ${q.title}`}
                      aria-current={qi === i ? "true" : undefined}
                    >
                      {String(qi + 1).padStart(2, "0")}
                    </button>
                  ))}
                </nav>
                <a className="kk-btn kk-btn--ghost kk-page__cta" href="/about">
                  Read the story
                  <FiArrowRight />
                </a>
                <span className="kk-page__folio">
                  {String(i + 1).padStart(2, "0")} — {String(EDUCATION.length).padStart(2, "0")}
                </span>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* ============================================================
          QUOTE
      ============================================================ */}
      <section className="kk-section kk-section--tight">
        <div className="kk-wrap">
          <motion.figure
            className="kk-quote"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8 }}
          >
            <blockquote>
              “Small steps <em>every day</em> lead to big results.”
            </blockquote>
            <figcaption>Karan Kumar — on consistency</figcaption>
          </motion.figure>
        </div>
      </section>

      {/* ============================================================
          CTA / CONTACT
      ============================================================ */}
      <section className="kk-cta" id="contact">
        <div className="kk-cta__orbs" aria-hidden="true">
          <span data-plx="0.1" />
          <span data-plx="0.18" />
        </div>

        <div className="kk-wrap kk-cta__inner">
          <span className="kk-eyebrow">Got an idea?</span>
          <h2 className="kk-h2">
            Let's build something <span className="kk-serif kk-acid">great</span> together.
          </h2>
          <p>
            Have a project or an idea in mind? I'd love to hear about it — always open to
            new opportunities and interesting problems.
          </p>

          <div className="kk-cta__actions">
            <Magnetic>
              <a className="kk-btn kk-btn--lg" href="mailto:karanksxxx@gmail.com">
                Start a project
                <FiArrowUpRight />
              </a>
            </Magnetic>
            <Magnetic>
              <a
                className="kk-btn kk-btn--ghost kk-btn--lg"
                href="https://wa.me/919041429065"
                target="_blank"
                rel="noopener noreferrer"
              >
                Chat on WhatsApp
                <FaWhatsapp />
              </a>
            </Magnetic>
          </div>

          <div className="kk-contact">
            <div className="kk-contact__info">
              <a className="kk-info" href="mailto:karanksxxx@gmail.com">
                <span className="kk-info__ic">
                  <FiMail />
                </span>
                <span>
                  <em>Email</em>
                  <b>karanksxxx@gmail.com</b>
                </span>
              </a>
              <a
                className="kk-info"
                href="https://wa.me/919041429065"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="kk-info__ic">
                  <FaWhatsapp />
                </span>
                <span>
                  <em>WhatsApp</em>
                  <b>+91 90414 29065</b>
                </span>
              </a>
              <div className="kk-info">
                <span className="kk-info__ic">
                  <FiMapPin />
                </span>
                <span>
                  <em>Location</em>
                  <b>Bathinda, Punjab, India</b>
                </span>
              </div>

              <div className="kk-contact__socials">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    title={s.label}
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            <form className="kk-form" onSubmit={submitForm}>
              <div className="kk-field">
                <label htmlFor="kk-name">Your name</label>
                <input
                  id="kk-name"
                  placeholder="Karan's next client"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="kk-field">
                <label htmlFor="kk-email">Email</label>
                <input
                  id="kk-email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
              <div className="kk-field kk-field--full">
                <label htmlFor="kk-msg">Project details</label>
                <textarea
                  id="kk-msg"
                  rows={4}
                  placeholder="What are you building, and what does done look like?"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                />
              </div>

              <div className="kk-form__foot">
                <button className="kk-btn kk-btn--lg" type="submit">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={sent ? "sent" : "idle"}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {sent ? "Opening email app…" : "Send message"}
                    </motion.span>
                  </AnimatePresence>
                  <FiSend />
                </button>
                <span className="kk-form__note">
                  Opens your email app pre-filled — nothing is stored.
                </span>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* WhatsApp float */}
      <a
        className="kk-wa"
        href="https://wa.me/919041429065"
        target="_blank"
        rel="noopener noreferrer"
        title="Chat on WhatsApp"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>
      <Navbar/>
<FooterKaranks1436/>
    </div>
  );
}